import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { MdSearch } from 'react-icons/md'
import Navbar from './Navbar'

export default function Tenants() {
  const { tenants = [] } = useSelector(state => state)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredTenants = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    if (!query) return tenants

    return tenants.filter(tenant =>
      [
        tenant.fullname,
        tenant.full_name,
        tenant.email,
        tenant.phone_number,
        tenant.phone,
        tenant.bvn
      ].some(value => String(value || '').toLowerCase().includes(query))
    )
  }, [tenants, searchQuery])

  const formatCurrency = amount =>
    `₦${Number(amount || 0).toLocaleString()}`

  const maskBvn = bvn => {
    if (!bvn) return '—'
    const value = String(bvn)
    if (value.length <= 4) return value
    return `${'*'.repeat(Math.max(value.length - 4, 0))}${value.slice(-4)}`
  }

  return (
    <div className="flex min-h-screen w-full bg-[#F7F9FC] font-[Satoshi]">
      <Navbar />

      <main className="min-w-0 flex-1 px-5 pb-10 pt-24 sm:px-6 lg:px-8 lg:pt-8">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-7">
            <p className="text-sm font-semibold text-green-600">CUSTOMERS</p>
            <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">Tenants</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Registered RentHub customers and their current repayment position.
                </p>
              </div>

              <div className="w-fit rounded-full border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-600 shadow-sm">
                {tenants.length} customer{tenants.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="relative max-w-2xl">
              <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
              <input
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder="Search by name, email, phone or BVN..."
                className="h-12 w-full rounded-xl border border-gray-300 pl-11 pr-4 text-sm outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-100"
              />
            </div>
          </div>

          {filteredTenants.length > 0 ? (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-[1100px] w-full text-left text-sm">
                  <thead className="bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-5 py-4">Customer</th>
                      <th className="px-5 py-4">Phone</th>
                      <th className="px-5 py-4">BVN</th>
                      <th className="px-5 py-4">Loan status</th>
                      <th className="px-5 py-4">Outstanding</th>
                      <th className="px-5 py-4">Installments left</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredTenants.map((tenant, index) => {
                      const outstanding = Number(tenant.outstanding_balance || 0)
                      const hasActiveLoan = outstanding > 0 || Boolean(tenant.active_loan)

                      return (
                        <tr
                          key={tenant.id || tenant.email || index}
                          className="border-t border-gray-100 transition hover:bg-gray-50/80"
                        >
                          <td className="px-5 py-4">
                            <p className="font-semibold text-gray-900">
                              {tenant.fullname || tenant.full_name || 'Unnamed customer'}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">{tenant.email || '—'}</p>
                          </td>

                          <td className="px-5 py-4 text-gray-600">
                            {tenant.phone_number || tenant.phone || '—'}
                          </td>

                          <td className="px-5 py-4 font-mono text-xs text-gray-500">
                            {maskBvn(tenant.bvn)}
                          </td>

                          <td className="px-5 py-4">
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase ${
                              hasActiveLoan
                                ? 'border-green-100 bg-green-50 text-green-700'
                                : 'border-gray-200 bg-gray-50 text-gray-500'
                            }`}>
                              {hasActiveLoan ? 'Active loan' : 'No active loan'}
                            </span>
                          </td>

                          <td className="px-5 py-4 font-semibold text-gray-900">
                            {formatCurrency(tenant.outstanding_balance)}
                          </td>

                          <td className="px-5 py-4 text-gray-600">
                            {tenant.installments_left ?? '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
              <p className="text-lg font-semibold text-gray-700">No tenants found</p>
              <p className="mt-2 text-sm text-gray-400">Registered customers will appear here.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
